// Choix de la catégorie affichée sur un aperçu de message.
//
// Deux champs concurrents arrivaient de l'API :
//   - `nature`       - NatureCourrier.java, le champ sur lequel les dossiers
//                      thématiques (Relances, Garanties, Expertises, BAP) filtrent ;
//   - `typeTemplate` - TypeTemplateMailEnum.java, le modèle utilisé à la rédaction,
//                      null dès que le mail est écrit librement.
//
// La liste affichait `typeTemplate`. Un mail libre catégorisé RELANCE tombait donc
// dans le dossier Relances sans porter la moindre étiquette, et l'étiquette pouvait
// contredire le dossier. La catégorie affichée doit être celle qui range le message.

import { avecLibelleTraduit } from '../../i18n/libelles';

/** Valeur par défaut de NatureCourrier : « non catégorisé », rien à afficher. */
const NATURE_NEUTRE = 'AUTRE';

/**
 * @param msg aperçu brut renvoyé par l'API
 * @returns la catégorie à afficher, ou null s'il n'y a rien de signifiant à montrer
 */
export function categorieAffichee(msg) {
  if (!msg) return null;
  const nature = msg.nature;
  if (nature && nature !== NATURE_NEUTRE) return nature;
  // Repli sur le modèle : utile pour les réponses d'une API antérieure à l'ajout du
  // champ `nature`, et pour les modèles sans nature équivalente (PROFORMA_ATTESTATION).
  // LIBRE désigne l'absence de modèle : ce n'est pas une catégorie.
  if (msg.typeTemplate && msg.typeTemplate !== 'LIBRE') return msg.typeTemplate;
  return null;
}

/**
 * Catégories métier, dans l'ordre où on les propose à l'agent : les plus courantes
 * d'abord, « Autre » en dernier puisqu'elle signifie l'absence de classement.
 *
 * Miroir de NatureCourrier.java. Cette liste vivait en double dans SearchOverlay et
 * ComposeWindow ; le filtre de recherche et le sélecteur du lecteur doivent afficher
 * les mêmes libellés, sans quoi un agent cherche « Relance » et ne retrouve pas ce
 * qu'il vient de classer.
 */
export const NATURES_COURRIER = avecLibelleTraduit([
  { value: 'RELANCE' },
  { value: 'DEMANDE_PIECES' },
  { value: 'DEMANDE_OFFRE' },
  { value: 'TRANSMISSION_EXPERTISE' },
  { value: 'CONFIRMATION_GARANTIE' },
  { value: 'ACCORD_VICTIME' },
  { value: 'BAP' },
  { value: 'TRANSMISSION_PV' },
  { value: 'OFFRE_INDEMNISATION' },
  { value: 'COMPTE_RENDU_AUDIENCE' },
  { value: 'AUTRE' },
], 'messagerie:natures');

/** Libellé lisible d'une catégorie, ou le code brut si elle n'est pas répertoriée. */
export function libelleNature(code) {
  if (!code) return null;
  return NATURES_COURRIER.find((n) => n.value === code)?.label || code;
}
